import { createContext, useContext, useEffect, useState } from 'react'

import { MapMarkerProps } from '../../components/MapCard/map-card.types'
import { GlobalDataContext } from '../../contexts/globalData'
import { CompanySegment } from '../../sdk/common/company.enum'
import {
  ICompanyProvidersResponse,
  ICompanyProvidersResponseDTO,
  ICompanyRanking,
  IProviderDashboard,
} from '../../sdk/company/company.types'
import { sdk } from '../../services/sdk'
import { IPaginatedResponse } from '../../types/paginated-response.type'

interface ProviderDashboardDataContext {
  firstProviders: ICompanyRanking[]
  firstClients: ICompanyRanking[]

  providers: ICompanyRanking[]
  clients: ICompanyRanking[]

  providersPages: number
  clientsPages: number

  providersWithRisks: ICompanyProvidersResponseDTO[]
  providersWithRisksCount: number

  providersLocations: MapMarkerProps[]

  dashboardData?: IProviderDashboard

  actions: {
    fetchInitialData: () => Promise<void>
    fetchProvidersRanking: (
      page: number,
    ) => Promise<IPaginatedResponse<ICompanyRanking[]>>
    fetchClientsRanking: (
      page: number,
    ) => Promise<IPaginatedResponse<ICompanyRanking[]>>
    fetchProvidersWithRisks: (page: number) => Promise<void>
    fetchProvidersLocations: () => Promise<void>
    fetchDashboardData: () => Promise<void>
  }
}

interface ProviderDashboardProviderProps {
  children: React.ReactNode
}

export const ProviderDashboardContext = createContext(
  {} as ProviderDashboardDataContext,
)

export function ProviderDashboardProvider(
  props: ProviderDashboardProviderProps,
) {
  const [firstProviders, setFirstProviders] = useState<ICompanyRanking[]>([])
  const [firstClients, setFirstClients] = useState<ICompanyRanking[]>([])

  const [providers, setProviders] = useState<ICompanyRanking[]>([])
  const [clients, setClients] = useState<ICompanyRanking[]>([])

  const [providersPages, setProvidersPages] = useState(0)
  const [clientsPages, setClientsPages] = useState(0)

  const [providersWithRisks, setProvidersWithRisks] = useState<
    ICompanyProvidersResponseDTO[]
  >([])
  const [providersWithRisksCount, setProvidersWithRisksCount] = useState(0)

  const [allProviders, setAllProviders] = useState<ICompanyProvidersResponse[]>(
    [],
  )
  const [providersLocations, setProvidersLocations] = useState<
    MapMarkerProps[]
  >([])

  const [dashboardData, setDashboardData] = useState<IProviderDashboard>()

  const { allCompanySegmentType } = useContext(GlobalDataContext)

  const fetchProvidersRanking = async (page: number) => {
    const _response = await sdk.company.getFirsttierProvidersRanking(page)
    setProviders(_response.data)
    setProvidersPages(Math.ceil(_response.count / 5))

    return _response
  }

  const fetchClientsRanking = async (page: number) => {
    const _response = await sdk.company.getFirsttierClientsRanking(page)
    setClients(_response.data)
    setClientsPages(Math.ceil(_response.count / 5))

    return _response
  }

  const fetchInitialData = async () => {
    const _providers = await fetchProvidersRanking(1)
    const _clients = await fetchClientsRanking(1)

    setFirstProviders(_providers.data)
    setFirstClients(_clients.data)
  }

  const fetchProvidersWithRisks = async (page: number) => {
    const _response = await sdk.company.getFirsttierWithRisk(page)

    setProvidersWithRisks(_response.data)
    setProvidersWithRisksCount(_response.count)
  }

  useEffect(() => {
    setProvidersLocations(
      allProviders.map((provider) => {
        const companySegment =
          allCompanySegmentType[provider.segment as CompanySegment]

        return {
          title: provider.name,
          lat: provider.address?.geoCoords?.coords?.lat ?? 0,
          lng: provider.address?.geoCoords?.coords?.lng ?? 0,
          color: companySegment?.color ?? '#000000',
          description: companySegment?.translate ?? '',
        }
      }),
    )
  }, [allProviders, allCompanySegmentType])

  const fetchProvidersLocations = async () => {
    const { data } = await sdk.company.getFirsttierProviders()

    setAllProviders(data)
  }

  const fetchDashboardData = async () => {
    const data = await sdk.company.getDashboardData()

    setDashboardData(data)
  }

  return (
    <ProviderDashboardContext.Provider
      value={{
        firstProviders,
        firstClients,
        providers,
        clients,
        providersPages,
        clientsPages,
        providersWithRisks,
        providersWithRisksCount,
        providersLocations,
        dashboardData,

        actions: {
          fetchInitialData,
          fetchProvidersRanking,
          fetchClientsRanking,
          fetchProvidersWithRisks,
          fetchProvidersLocations,
          fetchDashboardData,
        },
      }}
    >
      {props.children}
    </ProviderDashboardContext.Provider>
  )
}
